import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

interface Affiliate {
  id: string;
  name: string;
  email: string;
  referralCode: string;
  tenantId: string;
  status: string;
  avatarUrl?: string | null;
  phone?: string;
  company?: string;
  website?: string;
  smartLinksCount?: number;
  commissionType?: string;
  createdAt?: string;
}

interface AuthState {
  affiliate: Affiliate | null;
  accessToken: string | null;
  tenantSlug: string | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, tenantSlug: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  updateAffiliate: (data: Partial<Affiliate>) => void;
  setTenantSlug: (slug: string) => void;
}

interface RegisterData {
  tenantSlug: string;
  name: string;
  email: string;
  password: string;
  phone?: string;
  company?: string;
  website?: string;
  referralKey?: string;
}

// Helper to resolve tenant slug to UUID
async function resolveTenantSlug(slug: string): Promise<string> {
  const response = await api.get(`/tenants/by-slug/${slug}`);
  if (response.data.success && response.data.data?.id) {
    return response.data.data.id;
  }
  throw new Error('Tenant not found. Please check your tenant code.');
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      affiliate: null,
      accessToken: null,
      tenantSlug: null,
      tenantId: null,
      isAuthenticated: false,

      setTenantSlug: (slug: string) => {
        set({ tenantSlug: slug });
      },

      login: async (email: string, password: string, tenantSlug: string) => {
        // Resolve tenant slug to UUID
        const tenantId = await resolveTenantSlug(tenantSlug);

        const response = await api.post('/auth/affiliate/login', {
          email,
          password,
          tenantId,
        });

        const { affiliate, accessToken, refreshToken } = response.data;

        localStorage.setItem('refreshToken', refreshToken);

        set({
          affiliate,
          accessToken,
          tenantSlug,
          tenantId,
          isAuthenticated: true,
        });
      },

      register: async (data: RegisterData) => {
        // Resolve tenant slug to UUID
        const tenantId = await resolveTenantSlug(data.tenantSlug);

        await api.post('/auth/affiliate/register', {
          tenantId,
          name: data.name,
          email: data.email,
          password: data.password,
          phone: data.phone,
          company: data.company,
          website: data.website,
          referralKey: data.referralKey,
        });
      },

      logout: () => {
        api.post('/auth/logout').catch(() => {});
        localStorage.removeItem('refreshToken');
        set({
          affiliate: null,
          accessToken: null,
          isAuthenticated: false,
        });
      },

      refreshAuth: async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          get().logout();
          return;
        }

        try {
          const response = await api.post('/auth/refresh', { refreshToken });
          const { accessToken, refreshToken: newRefreshToken } = response.data;

          localStorage.setItem('refreshToken', newRefreshToken);
          set({ accessToken });
        } catch {
          get().logout();
        }
      },

      updateAffiliate: (data: Partial<Affiliate>) => {
        const currentAffiliate = get().affiliate;
        if (currentAffiliate) {
          set({ affiliate: { ...currentAffiliate, ...data } });
        }
      },
    }),
    {
      name: 'affiliate-auth-storage',
      partialize: (state) => ({
        affiliate: state.affiliate,
        accessToken: state.accessToken,
        tenantSlug: state.tenantSlug,
        tenantId: state.tenantId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

export default useAuthStore;
